import type { ListingQueryParams } from '../api/listings';

// ─── Basis-Typen ──────────────────────────────────────────────────────────────

export type ZuordnungValue = 'any' | 'has' | 'missing';
export type ZuordnungKey = 'adresse' | 'haus' | 'einheit' | 'vermieter' | 'verfahren';
export type OnlineFilterValue = 'alle' | 'online' | 'offline';

export interface SelectedVermieter {
  id: number;
  name: string;
}

export interface InserateFilter {
  states: string[];
  stateMode: 'include' | 'exclude';
  isOnline: OnlineFilterValue;
  adresse: ZuordnungValue;
  haus: ZuordnungValue;
  einheit: ZuordnungValue;
  vermieter: ZuordnungValue;
  verfahren: ZuordnungValue;
  selectedVermieter: SelectedVermieter[];
  addressSearch: string;
  zipcodeSearch: string;
  citySearch: string;
  selectedPlatforms: string[];
  selectedListingTypes: string[];
  priceMin: string;
  priceMax: string;
  ratingMin: string;
  ratingMax: string;
  sizeMin: string;
  sizeMax: string;
  guestsMin: string;
  guestsMax: string;
  bedroomsMin: string;
  bedroomsMax: string;
  bookedFrom: string;
  bookedTo: string;
  bookedMinNights: string;
  bookedMaxNights: string;
}

// ─── Konstanten ───────────────────────────────────────────────────────────────

export const DEFAULT_FILTER: InserateFilter = {
  states: [],
  stateMode: 'include',
  isOnline: 'alle',
  adresse: 'any',
  haus: 'any',
  einheit: 'any',
  vermieter: 'any',
  verfahren: 'any',
  selectedVermieter: [],
  addressSearch: '',
  zipcodeSearch: '',
  citySearch: '',
  selectedPlatforms: [],
  selectedListingTypes: [],
  priceMin: '',
  priceMax: '',
  ratingMin: '',
  ratingMax: '',
  sizeMin: '',
  sizeMax: '',
  guestsMin: '',
  guestsMax: '',
  bedroomsMin: '',
  bedroomsMax: '',
  bookedFrom: '',
  bookedTo: '',
  bookedMinNights: '',
  bookedMaxNights: '',
};

export const INSERATE_STATE_ORDER = [
  'neu',
  'beobachtet',
  'verdachtsfall',
  'verfahren_eingeleitet',
  'kein_verstoss',
];

export const ZUORDNUNG_ROWS: { key: ZuordnungKey; label: string }[] = [
  { key: 'adresse', label: 'Adresse' },
  { key: 'haus', label: 'Haus' },
  { key: 'einheit', label: 'Objekt (Einheit)' },
  { key: 'vermieter', label: 'Vermieter' },
  { key: 'verfahren', label: 'Verfahren' },
];

// ─── Badge-Typ ────────────────────────────────────────────────────────────────

export interface InserateBadgeCounts {
  statusBadge: number;
  onlineBadge: number;
  vermieterBadge: number;
  lageBadge: number;
  objektBadge: number;
  buchungenBadge: number;
  zuordnungBadge: number;
}

// ─── Pure Hilfsfunktionen ─────────────────────────────────────────────────────

export const toNum = (s: string): number | undefined =>
  s.trim() === '' ? undefined : Number(s);

export function isFilterActive(f: InserateFilter): boolean {
  return (
    f.states.length > 0 ||
    f.isOnline !== 'alle' ||
    f.adresse !== 'any' ||
    f.haus !== 'any' ||
    f.einheit !== 'any' ||
    f.vermieter !== 'any' ||
    f.verfahren !== 'any' ||
    f.selectedVermieter.length > 0 ||
    f.addressSearch !== '' ||
    f.zipcodeSearch !== '' ||
    f.citySearch !== '' ||
    f.selectedPlatforms.length > 0 ||
    f.selectedListingTypes.length > 0 ||
    f.priceMin !== '' ||
    f.priceMax !== '' ||
    f.ratingMin !== '' ||
    f.ratingMax !== '' ||
    f.sizeMin !== '' ||
    f.sizeMax !== '' ||
    f.guestsMin !== '' ||
    f.guestsMax !== '' ||
    f.bedroomsMin !== '' ||
    f.bedroomsMax !== '' ||
    (f.bookedFrom !== '' &&
      f.bookedTo !== '' &&
      (f.bookedMinNights !== '' || f.bookedMaxNights !== ''))
  );
}

export function buildFilterParams(filter: InserateFilter): ListingQueryParams {
  const p: ListingQueryParams = {};

  if (filter.states.length > 0) {
    p.states = filter.states;
    p.state_mode = filter.stateMode;
  }

  if (filter.isOnline === 'online') p.is_online = true;
  else if (filter.isOnline === 'offline') p.is_online = false;

  for (const key of ['adresse', 'haus', 'einheit', 'vermieter', 'verfahren'] as ZuordnungKey[]) {
    const val = filter[key] as ZuordnungValue;
    const param = `has_${key}` as keyof ListingQueryParams;
    if (val === 'has') (p as Record<string, unknown>)[param] = true;
    if (val === 'missing') (p as Record<string, unknown>)[param] = false;
  }

  if (filter.selectedVermieter.length > 0)
    p.vermieter_ids = filter.selectedVermieter.map(v => v.id);

  if (filter.addressSearch) p.address_search = filter.addressSearch;
  if (filter.zipcodeSearch) p.zipcode_search = filter.zipcodeSearch;
  if (filter.citySearch) p.city_search = filter.citySearch;

  if (filter.selectedPlatforms.length > 0) p.platforms = filter.selectedPlatforms;
  if (filter.selectedListingTypes.length > 0) p.listing_types = filter.selectedListingTypes;

  const priceMin = toNum(filter.priceMin);
  const priceMax = toNum(filter.priceMax);
  const ratingMin = toNum(filter.ratingMin);
  const ratingMax = toNum(filter.ratingMax);
  const sizeMin = toNum(filter.sizeMin);
  const sizeMax = toNum(filter.sizeMax);
  const guestsMin = toNum(filter.guestsMin);
  const guestsMax = toNum(filter.guestsMax);
  const bedroomsMin = toNum(filter.bedroomsMin);
  const bedroomsMax = toNum(filter.bedroomsMax);

  if (priceMin !== undefined) p.price_min = priceMin;
  if (priceMax !== undefined) p.price_max = priceMax;
  if (ratingMin !== undefined) p.rating_min = ratingMin;
  if (ratingMax !== undefined) p.rating_max = ratingMax;
  if (sizeMin !== undefined) p.size_min = sizeMin;
  if (sizeMax !== undefined) p.size_max = sizeMax;
  if (guestsMin !== undefined) p.guests_min = guestsMin;
  if (guestsMax !== undefined) p.guests_max = guestsMax;
  if (bedroomsMin !== undefined) p.bedrooms_min = bedroomsMin;
  if (bedroomsMax !== undefined) p.bedrooms_max = bedroomsMax;

  if (
    filter.bookedFrom &&
    filter.bookedTo &&
    (filter.bookedMinNights || filter.bookedMaxNights)
  ) {
    p.booked_from = filter.bookedFrom;
    p.booked_to = filter.bookedTo;
    if (filter.bookedMinNights) p.booked_min_nights = Number(filter.bookedMinNights);
    if (filter.bookedMaxNights) p.booked_max_nights = Number(filter.bookedMaxNights);
  }

  return p;
}

export function buildInitialFilter(searchParams: URLSearchParams): InserateFilter {
  const state = searchParams.get('state');
  const isOnlineParam = searchParams.get('is_online');
  if (state || isOnlineParam) {
    return {
      ...DEFAULT_FILTER,
      states: state ? [state] : [],
      isOnline: isOnlineParam === 'true' ? 'online' : isOnlineParam === 'false' ? 'offline' : 'alle',
    };
  }
  return DEFAULT_FILTER;
}

export function computeBadgeCounts(filter: InserateFilter): InserateBadgeCounts {
  const rangeKeys: (keyof Pick<
    InserateFilter,
    | 'priceMin' | 'priceMax'
    | 'ratingMin' | 'ratingMax'
    | 'sizeMin' | 'sizeMax'
    | 'guestsMin' | 'guestsMax'
    | 'bedroomsMin' | 'bedroomsMax'
  >)[][] = [
    ['priceMin', 'priceMax'],
    ['ratingMin', 'ratingMax'],
    ['sizeMin', 'sizeMax'],
    ['guestsMin', 'guestsMax'],
    ['bedroomsMin', 'bedroomsMax'],
  ];

  return {
    statusBadge: filter.states.length,
    onlineBadge: filter.isOnline !== 'alle' ? 1 : 0,
    vermieterBadge: filter.selectedVermieter.length,
    lageBadge: [filter.addressSearch, filter.zipcodeSearch, filter.citySearch].filter(Boolean).length,
    objektBadge:
      filter.selectedPlatforms.length +
      filter.selectedListingTypes.length +
      rangeKeys.filter(([min, max]) => filter[min] || filter[max]).length,
    buchungenBadge:
      filter.bookedFrom &&
      filter.bookedTo &&
      (filter.bookedMinNights || filter.bookedMaxNights)
        ? 1
        : 0,
    zuordnungBadge: ZUORDNUNG_ROWS.filter(r => filter[r.key] !== 'any').length,
  };
}
